import React, { useEffect, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { createPropertyFeature, updatePropertyFeature } from "graphql/mutations";
import { useAuth } from "context/AuthContext";
import { usePropertyData } from "context/PropertyDataContext";
import { notify } from "utilities/notify";
import Card from "components/common/Card";
import FormGroup from "components/common/FormGroup";

export default function Relations(props) {
  const { className, autorizedUser, setHasUnsavedChanges, handleFieldChange , updateFormCompletion } = props;
  const { propertyData, refresh } = usePropertyData();
  const { user } = useAuth();

  const [formData, setFormData] = useState([{}]);
  const [executedOnce, setExecutedOnce] = useState(false);
  const [asistenciaPfID, setAsistenciaPfID] = useState(null);
  const [aliadosPfID, setAliadosPfID] = useState(null);
  const [grupoPfID, setGrupoPfID] = useState(null);
   const [changedFields, setChangedFields] = useState({});

   const productFeaturesGroup = [
    "projectRelationsTechnicalAssitance",
    "projectRelationsStrategicAllies",
    "projectRelationsCommunityGroups",
  ];

  /** ✅ Verifica si todos los campos están completos */
  const checkFormCompletion = () => {
    if (!executedOnce) return;
    const hasAnyData = productFeaturesGroup.some((feature) => formData[feature]?.trim() !== "");
    updateFormCompletion(hasAnyData); // ✅ Se envía `true` si hay datos, sino `false`
  };
  

  useEffect(() => {
      if (executedOnce && Object.keys(formData).length > 0) {
        checkFormCompletion();
      }
    }, [formData, executedOnce]);

  useEffect(() => {
    if (propertyData && propertyData.propertyFeatures && user && !executedOnce) {
      const pfIDAsistencia =
        propertyData.propertyFeatures.filter((item) => {
          return item.featureID === "H_asistance_desc";
        })[0]?.id || null;
      setAsistenciaPfID(pfIDAsistencia);

      const pfIDAliados =
        propertyData.propertyFeatures.filter((item) => {
          return item.featureID === "H_aliados_estrategicos_desc";
        })[0]?.id || null;
      setAliadosPfID(pfIDAliados);

      const pfIDGrupo =
        propertyData.propertyFeatures.filter((item) => {
          return item.featureID === "H_grupo_comunitario_desc";
        })[0]?.id || null;
      setGrupoPfID(pfIDGrupo);

      setFormData((prevState) => ({
        ...prevState,
        projectRelationsTechnicalAssitance:
          propertyData.projectRelations?.technicalAssistance,
        projectRelationsStrategicAllies:
          propertyData.projectRelations?.strategicAllies,
        projectRelationsCommunityGroups:
          propertyData.projectRelations?.communityGroups,
      }));
      setExecutedOnce(true);
    }
  }, [propertyData, user]);

  const handleChangeInputValue = (e) => {
    const { name, value } = e.target;
  
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  
    setChangedFields((prev) => {
      const newChangedFields = { ...prev, [name]: true };
      return newChangedFields;
    });
  
    setHasUnsavedChanges(true);
    handleFieldChange(name, true);
    checkFormCompletion();
  };
  

  const handleSaveBtn = async () => {
  try {
    const updates = [
      {
        id: asistenciaPfID,
        featureID: "H_asistance_desc",
        value: formData.projectRelationsTechnicalAssitance,
        setID: setAsistenciaPfID,
      },
      {
        id: aliadosPfID,
        featureID: "H_aliados_estrategicos_desc",
        value: formData.projectRelationsStrategicAllies,
        setID: setAliadosPfID,
      },
      {
        id: grupoPfID,
        featureID: "H_grupo_comunitario_desc",
        value: formData.projectRelationsCommunityGroups,
        setID: setGrupoPfID,
      },
    ];

    // Recorremos cada campo y actualizamos o creamos el registro según corresponda
    for (const update of updates) {
      if (update.value) {
        if (update.id) {
          await API.graphql(
            graphqlOperation(updatePropertyFeature, {
              input: { id: update.id, value: update.value },
            })
          );
        } else {
          const response = await API.graphql(
            graphqlOperation(createPropertyFeature, {
              input: {
                propertyID: propertyData.propertyInfo.id,
                featureID: update.featureID,
                value: update.value,
              },
            })
          );
          update.setID(response.data.createPropertyFeature.id);
        }
      }
    }
    updateFormCompletion(true);
    setChangedFields({});
    Object.keys(changedFields).forEach((key) => handleFieldChange(key, false));
    setHasUnsavedChanges(false);
    notify({ msg: "Información actualizada", type: "success" });
    refresh();
    checkFormCompletion();
  } catch (error) {
    console.error("Error al guardar:", error);
    notify({ msg: "Error al guardar la información", type: "error" });
  }
};


  return (
    <Card className={className}>
      <Card.Header
        title="Relaciones con entidades y aliados estratégicos"
        sep={true}
      />
      <Card.Body>
        <div className="row row-cols-1 row-cols-md-2">
          <div className="col-12 col-lg-12">
            <FormGroup
              disabled={!autorizedUser}
              inputType="textarea"
              label="¿Recibe asistencia técnica en el predio?"
              inputName="projectRelationsTechnicalAssitance"
              inputValue={formData.projectRelationsTechnicalAssitance}
              saveBtnDisabled={
                propertyData.projectRelations?.technicalAssistance ===
                formData.projectRelationsTechnicalAssitance
                  ? true
                  : false
              }
              onChangeInputValue={(e) => handleChangeInputValue(e)}
              onClickSaveBtn={() =>
                handleSaveBtn("projectRelationsTechnicalAssitance")
              }
              className={`border border-terrasacha-light rounded-lg p-1 ${
                changedFields["projectRelationsTechnicalAssitance"] ? "border-terrasacha-danger bg-red-100" : ""
              }`}
            />
          </div>
          <div className="col-12 col-lg-12">
            <FormGroup
              disabled={!autorizedUser}
              inputType="textarea"
              label="¿Cuenta con aliados estratégicos?"
              inputName="projectRelationsStrategicAllies"
              inputValue={formData.projectRelationsStrategicAllies}
              saveBtnDisabled={
                propertyData.projectRelations?.strategicAllies ===
                formData.projectRelationsStrategicAllies
                  ? true
                  : false
              }
              onChangeInputValue={(e) => handleChangeInputValue(e)}
              onClickSaveBtn={() =>
                handleSaveBtn("projectRelationsStrategicAllies")
              }
              className={`border border-terrasacha-light rounded-lg p-1 ${
                changedFields["projectRelationsStrategicAllies"] ? "border-terrasacha-danger bg-red-100" : ""
              }`}
            />
          </div>
          <div className="col-12 col-lg-12">
            <FormGroup
              disabled={!autorizedUser}
              inputType="textarea"
              label="¿Pertenece a algún grupo comunitario?"
              inputName="projectRelationsCommunityGroups"
              inputValue={formData.projectRelationsCommunityGroups}
              saveBtnDisabled={
                propertyData.projectRelations?.communityGroups ===
                formData.projectRelationsCommunityGroups
                  ? true
                  : false
              }
              onChangeInputValue={(e) => handleChangeInputValue(e)}
              onClickSaveBtn={() =>
                handleSaveBtn("projectRelationsCommunityGroups")
              }
              className={`border border-terrasacha-light rounded-lg p-1 ${
                changedFields["projectRelationsCommunityGroups"] ? "border-terrasacha-danger bg-red-100" : ""
              }`}
            />
          </div>
        </div>
        {autorizedUser && (
          <div className="flex justify-center mt-6">
            <button
              type="button"
              className="btn-terrasacha-primary px-6 py-2.5 font-typographica"
              onClick={handleSaveBtn}
              aria-label="Guardar relaciones con entidades"
            >
              Guardar
            </button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
